import { __rest } from "tslib";
import EllipsisOutlined from '@haier/icons/EllipsisOutlined';
import classNames from 'classnames';
import RcMenu, { ItemGroup } from 'rc-menu';
import useEvent from 'rc-util/lib/hooks/useEvent';
import omit from 'rc-util/lib/omit';
import * as React from 'react';
import { forwardRef } from 'react';
import { ConfigContext } from '../config-provider';
import { SiderContext } from '../layout/Sider';
import useBuriedPoint from '../_util/hooks/useBuriedPoint';
import collapseMotion from '../_util/motion';
import { cloneElement } from '../_util/reactNode';
import warning from '../_util/warning';
import useItems from './hooks/useItems';
import MenuContext, { MenuTheme } from './MenuContext';
import MenuDivider from './MenuDivider';
import Item, { MenuItemProps } from './MenuItem';
import OverrideContext from './OverrideContext';
import SubMenu, { SubMenuProps } from './SubMenu';
export { MenuItemGroupProps } from 'rc-menu';
export { MenuDividerProps } from './MenuDivider';
export { MenuTheme, SubMenuProps, MenuItemProps };
const InternalMenu = forwardRef((props, ref) => {
    var _a;
    useBuriedPoint('Menu');
    const override = React.useContext(OverrideContext) || {};
    const { getPrefixCls, getPopupContainer, direction } = React.useContext(ConfigContext);
    const rootPrefixCls = getPrefixCls();
    const { prefixCls: customizePrefixCls, className, theme = 'light', expandIcon, _internalDisableMenuItemTitleTooltip, inlineCollapsed, siderCollapsed, items, children, mode, selectable, onClick } = props, restProps = __rest(props, ["prefixCls", "className", "theme", "expandIcon", "_internalDisableMenuItemTitleTooltip", "inlineCollapsed", "siderCollapsed", "items", "children", "mode", "selectable", "onClick"]);
    const passedProps = omit(restProps, ['collapsedWidth']);
    // ========================= Items ===========================
    const mergedChildren = useItems(items) || children;
    // ======================== Warning ==========================
    warning(!('inlineCollapsed' in props && mode !== 'inline'), 'Menu', '`inlineCollapsed` should only be used when `mode` is inline.');
    warning(!(props.siderCollapsed !== undefined && 'inlineCollapsed' in props), 'Menu', '`inlineCollapsed` not control Menu under Sider. Should set `collapsed` on Sider instead.');
    warning('items' in props && !children, 'Menu', '`children` will be removed in next major version. Please use `items` instead.');
    (_a = override.validator) === null || _a === void 0 ? void 0 : _a.call(override, { mode });
    // ========================== Click ==========================
    // Tell dropdown that item clicked
    const onItemClick = useEvent((...args) => {
        var _a;
        onClick === null || onClick === void 0 ? void 0 : onClick(...args);
        (_a = override === null || override === void 0 ? void 0 : override.onClick) === null || _a === void 0 ? void 0 : _a.call(override);
    });
    // ========================== Mode ===========================
    const mergedMode = override.mode || mode;
    // ======================= Selectable ========================
    const mergedSelectable = selectable !== null && selectable !== void 0 ? selectable : override.selectable;
    // ======================== Collapsed ========================
    // Inline Collapsed
    const mergedInlineCollapsed = React.useMemo(() => {
        if (siderCollapsed !== undefined) {
            return siderCollapsed;
        }
        return inlineCollapsed;
    }, [inlineCollapsed, siderCollapsed]);
    const defaultMotions = {
        horizontal: { motionName: `${rootPrefixCls}-slide-up` },
        inline: collapseMotion,
        other: { motionName: `${rootPrefixCls}-zoom-big` },
    };
    const prefixCls = getPrefixCls('menu', customizePrefixCls || override.prefixCls);
    const menuClassName = classNames(`${prefixCls}-${theme}`, className);
    // ====================== Expand Icon ========================
    let mergedExpandIcon;
    if (typeof expandIcon === 'function') {
        mergedExpandIcon = expandIcon;
    }
    else {
        mergedExpandIcon = cloneElement(expandIcon || override.expandIcon, {
            className: `${prefixCls}-submenu-expand-icon`,
        });
    }
    // ======================== Context ==========================
    const contextValue = React.useMemo(() => ({
        prefixCls,
        inlineCollapsed: mergedInlineCollapsed || false,
        antdMenuTheme: theme,
        direction,
        firstLevel: true,
        disableMenuItemTitleTooltip: _internalDisableMenuItemTitleTooltip,
    }), [prefixCls, mergedInlineCollapsed, theme, direction, _internalDisableMenuItemTitleTooltip]);
    // ========================= Render ==========================
    return (React.createElement(OverrideContext.Provider, { value: null },
        React.createElement(MenuContext.Provider, { value: contextValue },
            React.createElement(RcMenu, Object.assign({ getPopupContainer: getPopupContainer, overflowedIndicator: React.createElement(EllipsisOutlined, null), overflowedIndicatorPopupClassName: `${prefixCls}-${theme}`, mode: mergedMode, selectable: mergedSelectable, onClick: onItemClick }, passedProps, { inlineCollapsed: mergedInlineCollapsed, className: menuClassName, prefixCls: prefixCls, direction: direction, defaultMotions: defaultMotions, expandIcon: mergedExpandIcon, ref: ref }), mergedChildren))));
});
// We should keep this as ref-able
class Menu extends React.Component {
    constructor() {
        super(...arguments);
        this.focus = (options) => {
            var _a;
            (_a = this.menu) === null || _a === void 0 ? void 0 : _a.focus(options);
        };
    }
    render() {
        return (React.createElement(SiderContext.Consumer, null, (context) => (React.createElement(InternalMenu, Object.assign({ ref: (node) => {
                this.menu = node;
            } }, this.props, context)))));
    }
}
Menu.Divider = MenuDivider;
Menu.Item = Item;
Menu.SubMenu = SubMenu;
Menu.ItemGroup = ItemGroup;
export default Menu;
